import type { ButtonMode, LauncherShape } from '@feedback/shared';

/**
 * Public, safe-to-expose widget appearance config, mirrored from the
 * backend's `WidgetConfig` (see `apps/api/src/widget/widget.controller.ts`).
 */
export interface WidgetConfig {
  buttonMode: ButtonMode;
  iconUrl: string | null;
  label: string | null;
  bgColor: string | null;
  textColor: string | null;
  iconColor: string | null;
  position: string | null;
  hoverBgColor?: string | null;
  hoverTextColor?: string | null;
  borderRadius?: number | null;
  fontFamily?: string | null;
  linkColor?: string | null;
  /** Optional, like the other late-added fields: an older cached config object
   *  simply has no opinion, and the widget treats that as "on". */
  tooltipEnabled?: boolean;
  tooltipText?: string | null;
  /** Optional, like the other late-added fields — an older cached config
   *  simply has no opinion, and the widget treats that as a rectangle. */
  shape?: LauncherShape;
  /**
   * `BUTTON` or `EDGE_TAB`, from `GET /widget/config`. Optional because a
   * cached config from before the field has no opinion. Anything other than
   * `EDGE_TAB` renders the button. See `resolveLauncherStyle`.
   */
  launcherStyle?: string;
  /**
   * `EDGE_TAB` or `BOTTOM_BAR`, from `GET /widget/config`. Optional because a
   * cached config from before the field has no opinion. Anything other than
   * `BOTTOM_BAR` keeps the small edge tab on a phone. See `resolvePhoneLauncher`.
   */
  phoneLauncher?: string;
  /** Mask path relative to the API root, as sent by `GET /widget/config`. */
  shapeAssetPath?: string | null;
  /** Absolute mask url, resolved from `shapeAssetPath` by `App` against its
   *  `apiBase`. `Button` reads only this one. */
  shapeAssetUrl?: string | null;
  /**
   * Origin of the client portal the reporter can follow a report in, or null
   * when the application has no client-portal sign-in. The success step links
   * to the report only when this is set. Optional because an older server
   * sends nothing, which means no link.
   */
  clientPortalUrl?: string | null;
}

export type AttachmentKind = 'ATTACHMENT' | 'SCREENSHOT';

export interface UploadMeta {
  fileName: string;
  contentType: string;
  kind: AttachmentKind;
}

export interface UploadTarget {
  url: string;
  blobKey: string;
}

export interface SubmitResult {
  reference: string;
  id: string;
}

/**
 * Base class for all typed errors thrown by the ingest API client. Carries
 * the HTTP status and, when the backend responded with its standard
 * `{ error: { code, message } }` envelope, the machine-readable `code`.
 *
 * This module's job is to throw these typed errors for callers to catch —
 * NOT to swallow failures. The widget's fail-silent policy (never letting an
 * error escape into the host app) is enforced by the controller that calls
 * this client, not here.
 */
export class WidgetApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'WidgetApiError';
    this.status = status;
    this.code = code;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** Thrown for 401/403 responses — bad integration key or disallowed Origin. */
export class AuthError extends WidgetApiError {
  constructor(message = 'Authentication failed', status = 401, code?: string) {
    super(message, status, code);
    this.name = 'AuthError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** Thrown for 429 responses — the caller is being rate limited. */
export class RateLimitError extends WidgetApiError {
  constructor(message = 'Rate limited', status = 429, code?: string) {
    super(message, status, code);
    this.name = 'RateLimitError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** Thrown for any other non-2xx response. */
export class ApiError extends WidgetApiError {
  constructor(message: string, status: number, code?: string) {
    super(message, status, code);
    this.name = 'ApiError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}
